import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { actions as cartActions } from '../../../../store/ducks/cart.duck';
import { IAppState } from '../../../../store/rootDuck';
import { IStore } from '../../../../interfaces/store';
import { cleanPhone } from '../../../../utils/utils';
import { ILocation } from '../../../../interfaces/locations';
import { ICart, IGuestCart } from '../interfaces';
import { IUser } from '../../../../interfaces/user';
import { useBuyOrder } from './useBuyOrder';

export enum DeliveryType {
  self = 'self', // самовывоз сдэк
  delivery = 'delivery', // доставка сдэк
  self_store = 'self_store', // самовывоз со склада
}

const getInitialValues = (
  cart: ICart | null,
  me: IUser | undefined,
  countryData: any,
) => {
  return {
    deliveryType: cart?.delivery_type || DeliveryType.delivery,
    locationDelivery: {
      address: cart?.address || '',
      lng: 0,
      lat: 0,
      city: cart?.city,
      country: '',
      house: '',
      province: '',
      street: '',
      working_times: undefined,
      locality: '',
    },
    location: {
      id: undefined,
      address: cart?.point?.address || '',
      lng: cart?.point?.lng || 0,
      lat: cart?.point?.lat || 0,
      city: cart?.point?.city_name || '',
      code: cart?.point?.cdek_code || '',
      country: '',
      house: '',
      province: '',
      street: '',
      working_times: undefined,
    },
    phone:
      cart?.phone ||
      (me?.phone_numbers
        ? me && cleanPhone(me.phone_numbers, `+${countryData?.code || '7'}`)
        : undefined) ||
      '',
    fio: cart?.fio?.trim() || me?.first_name || '',
    email: cart?.email || me?.email || '',
    pointCode: '',
    city: cart?.city || '',
    cityFull: cart?.city || '',
    store: cart?.store || {},
  };
};

export const useFormikCart = ({
  countryData,
  setAuthDialogOpen,
  isOnceCompany,
}: {
  countryData: any;
  setAuthDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isOnceCompany: boolean;
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { fetchBuy } = useBuyOrder();
  const me = useSelector(({ profile }: IAppState) => profile.me, shallowEqual);
  const { cart, guestCart } = useSelector(
    ({ cart }: IAppState) => ({
      cart: cart.cart,
      guestCart: cart.guestCart,
    }),
    shallowEqual
  );
  const isAuthorized = useSelector(({ auth }: IAppState) => auth.user != null, shallowEqual);

  const formik = useFormik({
    initialValues: getInitialValues(
      cart,
      me,
      countryData,
    ),
    enableReinitialize: true,
    validationSchema: () =>
      Yup.lazy(values => {
        // eslint-disable-next-line no-nested-ternary
        return values.deliveryType === DeliveryType.self_store
          ? Yup.object().shape({
              store: Yup.object({
                id: Yup.number()
                  .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
                  .nullable(),
              }).nullable(),
              phone: Yup.string()
                .required(intl.formatMessage({ id: 'AUTH.INPUT.PHONE_REQUIRED' }))
                .test(
                  'valid-phone',
                  intl.formatMessage({ id: 'AUTH.INPUT.PHONE_INVALID' }),
                  val => {
                    if (!val) return false;

                    const cleaned = val.replace(/\+/g, '').replace(/[\s-()]/g, '');

                    return /^[0-9]{10,12}$/.test(cleaned);
                  }
                )
                .nullable(),
              email: Yup.string()
                .required(intl.formatMessage({ id: 'AUTH.INPUT.DATE_REQUIRED' }))
                .nullable(),
            })
          : values.deliveryType === DeliveryType.delivery
          ? Yup.object().shape({
              locationDelivery: Yup.object({
                address: Yup.string()
                  .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
                  .nullable(),
              }),
              city: Yup.string().required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })),
              phone: Yup.string()
                .required(intl.formatMessage({ id: 'AUTH.INPUT.PHONE_REQUIRED' }))
                .test(
                  'valid-phone',
                  intl.formatMessage({ id: 'AUTH.INPUT.PHONE_INVALID' }),
                  val => {
                    if (!val) return false;

                    const cleaned = val.replace(/\+/g, '').replace(/[\s-()]/g, '');

                    return /^[0-9]{10,12}$/.test(cleaned);
                  }
                )
                .nullable(),
              email: Yup.string()
                .required(intl.formatMessage({ id: 'AUTH.INPUT.DATE_REQUIRED' }))
                .nullable(),
            })
          : Yup.object().shape({
              location: Yup.object({
                code: Yup.string()
                  .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
                  .nullable(),
              }),
              phone: Yup.string()
                .required(intl.formatMessage({ id: 'AUTH.INPUT.PHONE_REQUIRED' }))
                .test(
                  'valid-phone',
                  intl.formatMessage({ id: 'AUTH.INPUT.PHONE_INVALID' }),
                  val => {
                    if (!val) return false;

                    const cleaned = val.replace(/\+/g, '').replace(/[\s-()]/g, '');

                    return /^[0-9]{10,12}$/.test(cleaned);
                  }
                ),

              email: Yup.string().required(
                intl.formatMessage({ id: 'AUTH.INPUT.DATE_REQUIRED' })
              ),
            });
        // Yup.object().shape({
        //     location: Yup.object({
        //       address: Yup.string()
        //         .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
        //         .nullable(),
        //       phone: Yup.string()
        //         .required(intl.formatMessage({ id: 'AUTH.INPUT.PHONE_REQUIRED' }))
        //         .test(
        //           'valid-phone',
        //           intl.formatMessage({ id: 'AUTH.INPUT.PHONE_INVALID' }),
        //           val => {
        //             if (!val) return false;

        //             const cleaned = val.replace(/\+/g, '').replace(/[\s-()]/g, '');

        //             return /^[0-9]{10,12}$/.test(cleaned);
        //           }
        //         )
        //         .nullable(),
        //       email: Yup.string()
        //         .required(intl.formatMessage({ id: 'AUTH.INPUT.DATE_REQUIRED' }))
        //         .nullable(),
        //     }),
        //   })
      }),
    onSubmit: async values => {
      if (!isAuthorized) {
        const { locationDelivery, location } = values;
        if (values.deliveryType === DeliveryType.delivery) {
          locationDelivery
            ? localStorage.setItem(
                'locationDelivery',
                JSON.stringify(
                  locationDelivery.address.trim().length > 0 ? locationDelivery : null
                )
              )
            : localStorage.removeItem('locationDelivery');
        } else {
          location
            ? localStorage.setItem('location', JSON.stringify(location))
            : localStorage.removeItem('location');
        }
        dispatch(
          cartActions.setInfoUserGuestCart({
            fio: values.fio || '',
            email: values.email || '',
            address:
              values.deliveryType === DeliveryType.delivery
                ? values.locationDelivery.address
                : values.location.address,
            phone: values.phone || '',
            city: values.city,
            pointCode: values.pointCode,
          })
        );
        setAuthDialogOpen(true);
      } else if (cart?.id) {
        if (isOnceCompany && cart.store && values.deliveryType === DeliveryType.self_store) {
          dispatch(
            cartActions.checkoutRequest({
              data: {
                // ...values,
                phone: `+${countryData?.code}${values.phone}` || '',
                fio: values.fio || '',
                email: values.email || '',
              },
            })
          );
        } else {
          fetchBuy(cart?.id, () => {
            dispatch(
              cartActions.checkoutRequest({
                data: {
                  // ...values,
                  phone: `+${countryData?.code}${values.phone}` || '',
                  fio: values.fio || '',
                  email: values.email || '',
                },
              })
            );
          });
        }
        localStorage.removeItem('locationDelivery');
      }
    },
  });
  return formik;
};
